import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatInTimeZone } from "date-fns-tz";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { CohortsKeys, getAllCohorts } from "@/features/cohorts/api/cohorts";

import { ApplicantsKeys, updateApplicantCohort } from "../api/applicants";

interface ChangeCohortDialogProps {
  applicant: {
    id: string;
    name: string;
    currentCohortId?: string | null;
  } | null;
  onClose: () => void;
}

const ChangeCohortDialog: React.FC<ChangeCohortDialogProps> = ({
  applicant,
  onClose,
}) => {
  const [selectedCohortId, setSelectedCohortId] = useState<string>("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: cohorts, isLoading: cohortsLoading } = useQuery({
    queryKey: [CohortsKeys.GET_COHORTS],
    queryFn: getAllCohorts,
    enabled: !!applicant,
  });

  const { mutate: changeCohort, isPending } = useMutation({
    mutationFn: ({
      applicantId,
      cohortId,
    }: {
      applicantId: string;
      cohortId: string;
    }) => updateApplicantCohort(applicantId, cohortId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ApplicantsKeys.GET_APPLICANTS],
      });
      queryClient.invalidateQueries({
        queryKey: [ApplicantsKeys.GET_APPLICANT, variables.applicantId],
      });
      toast({
        title: "Success",
        description: "Cohort updated successfully",
      });
      onClose();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update cohort",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedCohortId && applicant) {
      changeCohort({ applicantId: applicant.id, cohortId: selectedCohortId });
    }
  };

  // Set initial value when dialog opens or applicant changes
  React.useEffect(() => {
    if (applicant?.currentCohortId) {
      setSelectedCohortId(applicant.currentCohortId);
    } else {
      setSelectedCohortId("");
    }
  }, [applicant?.id, applicant?.currentCohortId]);

  const cohortsList = Array.isArray(cohorts) ? cohorts : [];

  return (
    <Dialog
      open={!!applicant}
      onOpenChange={(open) => {
        if (!open && !isPending) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Change Cohort</DialogTitle>
            <DialogDescription>
              Assign {applicant?.name} to a cohort
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {cohortsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="cohort">Select Cohort</Label>
                <Select
                  value={selectedCohortId}
                  onValueChange={setSelectedCohortId}
                >
                  <SelectTrigger id="cohort">
                    <SelectValue placeholder="Select a cohort" />
                  </SelectTrigger>
                  <SelectContent>
                    {cohortsList.map((cohort) => (
                      <SelectItem key={cohort._id} value={cohort._id}>
                        Cohort #{cohort.number} -{" "}
                        {formatInTimeZone(
                          cohort.startDate,
                          "America/New_York",
                          "MMM d, yyyy",
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {cohortsList.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No cohorts available
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPending || !selectedCohortId || cohortsLoading}
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeCohortDialog;
